import { useState, useRef, useEffect, useCallback, memo, Fragment } from 'react';
import { cn } from '@/lib/utils';
import { BuilderMessage } from '@/lib/api';
import { MessageBubble } from './MessageBubble';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Loader2,
  ChevronRight,
  Terminal,
  FileText,
  Wrench,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Cpu,
  Hammer,
} from 'lucide-react';
import type { VerboseEvent } from '../hooks/useBuilderSession';

interface ConversationPaneProps {
  messages: BuilderMessage[];
  isProcessing: boolean;
  suggestedNextSteps?: string[];
  /** Streaming text being received (nodeId -> accumulated text) */
  streamingText?: Record<string, string>;
  /** Committed verbose events keyed by assistant message timestamp */
  verboseHistory?: Record<string, VerboseEvent[]>;
  /** In-progress verbose events for the current response */
  pendingVerboseEvents?: VerboseEvent[];
  onSuggestionClick?: (suggestion: string) => void;
  /** Custom empty state content (replaces default greeting) */
  emptyStateContent?: React.ReactNode;
  /** Content rendered at the top of the message area (e.g. guided panels) */
  headerContent?: React.ReactNode;
  /** Content rendered below messages (e.g. guided controls) */
  footerContent?: React.ReactNode;
  className?: string;
}

/** Parsed structure from a tool result string */
export interface ParsedToolResult {
  primaryMessage: string;
  validationWarnings: string[];
  editBudgetWarning: string | null;
  verificationWarning: string | null;
  editCount: number | null;
}

/** Parse a tool result string into structured parts */
export function parseToolResultString(resultStr: string): ParsedToolResult {
  const parsed: ParsedToolResult = {
    primaryMessage: '',
    validationWarnings: [],
    editBudgetWarning: null,
    verificationWarning: null,
    editCount: null,
  };

  if (!resultStr) return parsed;

  const lines = resultStr.split('\n');
  const primaryLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Validation warnings: lines containing "unknown prop" or "missing required prop"
    if (line.includes('unknown prop') || line.includes('missing required prop')) {
      parsed.validationWarnings.push(line.trim());
      i++;
      continue;
    }

    // Edit budget warning: starts with "WARNING: You have made N edits"
    const editBudgetMatch = line.match(/^WARNING: You have made (\d+) edits/);
    if (editBudgetMatch) {
      parsed.editBudgetWarning = line.trim();
      parsed.editCount = parseInt(editBudgetMatch[1], 10);
      i++;
      continue;
    }

    // Verification warning: starts with "WARNING: You have made N changes without"
    if (line.match(/^WARNING: You have made \d+ changes without/)) {
      parsed.verificationWarning = line.trim();
      i++;
      continue;
    }

    primaryLines.push(line);
    i++;
  }

  parsed.primaryMessage = primaryLines.join('\n').trim();
  return parsed;
}

/** Check if a tool_result event has warnings (successful but with issues) */
export function hasWarnings(event: VerboseEvent): boolean {
  if (event.type !== 'tool_result') return false;
  if (event.data.isError) return false;

  const result = event.data.toolResult as Record<string, unknown> | undefined;
  if (!result || !result.success) return false;

  const resultStr = typeof result.result === 'string' ? result.result : '';
  if (!resultStr) return false;

  const parsed = parseToolResultString(resultStr);
  return (
    parsed.validationWarnings.length > 0 ||
    parsed.editBudgetWarning !== null ||
    parsed.verificationWarning !== null
  );
}

/** Renders a single verbose event */
const VerboseEventDisplay = memo(function VerboseEventDisplay({ event }: { event: VerboseEvent }) {
  const isError = event.type === 'tool_result' && event.data.isError;
  const isWarning = !isError && hasWarnings(event);

  // Auto-expand errors and warnings so users immediately see what went wrong
  const [isOpen, setIsOpen] = useState(isError || isWarning);

  const getIcon = () => {
    switch (event.type) {
      case 'system_prompt':
        return <FileText className="h-3.5 w-3.5 text-blue-500" />;
      case 'model_info':
        return <Cpu className="h-3.5 w-3.5 text-purple-500" />;
      case 'tools_available':
        return <Hammer className="h-3.5 w-3.5 text-teal-500" />;
      case 'tool_call':
        return <Wrench className="h-3.5 w-3.5 text-orange-500" />;
      case 'tool_result':
        if (isError) return <XCircle className="h-3.5 w-3.5 text-red-500" />;
        if (isWarning) return <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />;
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    }
  };

  const getTitle = () => {
    switch (event.type) {
      case 'system_prompt':
        return 'System Prompt';
      case 'model_info':
        return 'Model';
      case 'tools_available':
        return `Tools (${event.data.tools?.length || 0})`;
      case 'tool_call':
        return `Tool: ${event.data.toolName}`;
      case 'tool_result':
        return `Result: ${event.data.toolName}${event.data.isError ? ' (Error)' : ''}`;
    }
  };

  // Get a brief summary for the collapsed header
  const getSummary = () => {
    switch (event.type) {
      case 'system_prompt':
        return null;
      case 'tools_available': {
        const tools = event.data.tools;
        if (!tools || tools.length === 0) return null;
        return tools.map((t) => t.name).join(', ');
      }
      case 'model_info': {
        // Show a friendly model name
        const model = event.data.model || '';
        if (model.includes('opus')) return 'Opus 4.5 (Deep thinking)';
        if (model.includes('sonnet')) return 'Sonnet 4';
        if (model.includes('haiku')) return 'Haiku 3.5';
        return model;
      }
      case 'tool_call': {
        const input = event.data.toolInput as Record<string, unknown> | undefined;
        if (!input) return null;
        const toolName = event.data.toolName;
        if (toolName === 'edit_ui' && input.target) {
          const changes = input.changes as Record<string, unknown> | undefined;
          const ops = changes ? Object.keys(changes).join(', ') : '';
          return ops ? `target: ${input.target}, ${ops}` : `target: ${input.target}`;
        }
        if (toolName === 'add_component') {
          const type = input.type || '';
          const parentId = input.parentId || 'root';
          return type ? `${type} → ${parentId}` : null;
        }
        if (toolName === 'add_action') {
          const id = input.id || '';
          const type = input.type || '';
          return id ? `${id} (${type})` : null;
        }
        if (toolName === 'advance_build_step' && input.stepId) {
          return `stepId: ${input.stepId}`;
        }
        if (input.target) return `target: ${input.target}`;
        if (input.name) return `name: ${input.name}`;
        return null;
      }
      case 'tool_result': {
        const result = event.data.toolResult as Record<string, unknown> | undefined;
        if (!result) return null;
        if (result.error) {
          const errStr = String(result.error);
          if (errStr.startsWith('BLOCKED')) return 'BLOCKED';
          if (errStr.includes('verification FAILED')) {
            const attemptMatch = errStr.match(/attempt (\d+\/\d+)/);
            return attemptMatch
              ? `verification failed (${attemptMatch[1]})`
              : 'verification failed';
          }
          return errStr.length > 60 ? errStr.slice(0, 57) + '...' : errStr;
        }
        if (result.success) {
          const resultStr = typeof result.result === 'string' ? result.result : '';
          if (!resultStr) return 'success';
          const parsed = parseToolResultString(resultStr);
          const parts: string[] = [];
          if (parsed.validationWarnings.length > 0) {
            parts.push(
              `${parsed.validationWarnings.length} warning${parsed.validationWarnings.length > 1 ? 's' : ''}`,
            );
          }
          if (parsed.editCount !== null) {
            parts.push(`edit ${parsed.editCount}/12`);
          }
          if (parsed.verificationWarning) {
            parts.push('needs verification');
          }
          return parts.length > 0 ? `success (${parts.join(', ')})` : 'success';
        }
        return null;
      }
    }
  };

  const getContent = () => {
    switch (event.type) {
      case 'system_prompt':
        return event.data.prompt || '';
      case 'model_info':
        return `Model: ${event.data.model}\nIntent: ${event.data.intent}`;
      case 'tools_available': {
        const tools = event.data.tools;
        if (!tools || tools.length === 0) return 'No tools available';
        return tools.map((t) => `${t.name}\n  ${t.description}`).join('\n\n');
      }
      case 'tool_call':
        return JSON.stringify(event.data.toolInput, null, 2);
      case 'tool_result': {
        const result = event.data.toolResult as Record<string, unknown> | undefined;
        if (!result) return '';

        // For errors, show error string directly
        if (result.error) return String(result.error);

        const resultStr = typeof result.result === 'string' ? result.result : '';
        if (!resultStr) return JSON.stringify(result, null, 2);

        const parsed = parseToolResultString(resultStr);
        const sections: string[] = [];

        // Primary message
        if (parsed.primaryMessage) {
          sections.push(parsed.primaryMessage);
        }

        // Validation warnings section
        if (parsed.validationWarnings.length > 0) {
          sections.push(
            `--- Validation Warnings (${parsed.validationWarnings.length}) ---\n` +
              parsed.validationWarnings.map((w) => `  \u26A0 ${w}`).join('\n'),
          );
        }

        // Edit budget section
        if (parsed.editBudgetWarning) {
          sections.push(`--- Edit Budget ---\n  \u26A0 ${parsed.editBudgetWarning}`);
        }

        // Verification section
        if (parsed.verificationWarning) {
          sections.push(`--- Verification ---\n  \u26A0 ${parsed.verificationWarning}`);
        }

        return sections.join('\n\n');
      }
    }
  };

  const summary = getSummary();

  const triggerBg = isError
    ? 'bg-red-500/10 border-red-500/30'
    : isWarning
      ? 'bg-yellow-500/10 border-yellow-500/30'
      : 'bg-muted/50 border-border/50';

  const summaryColor = isError
    ? 'text-red-400'
    : isWarning
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-muted-foreground';

  const contentBg = isError
    ? 'bg-red-500/5 border-red-500/20'
    : isWarning
      ? 'bg-yellow-500/5 border-yellow-500/20'
      : 'bg-muted/30 border-border/30';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 w-full px-3 py-1.5 text-xs font-mono border rounded hover:bg-muted transition-colors text-left',
            triggerBg,
          )}
        >
          <ChevronRight
            className={cn('h-3 w-3 transition-transform flex-shrink-0', isOpen && 'rotate-90')}
          />
          {getIcon()}
          <span className="truncate">{getTitle()}</span>
          {summary && <span className={cn('truncate flex-1', summaryColor)}>— {summary}</span>}
          <Terminal className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre
          className={cn(
            'mt-1 p-2 text-xs font-mono border rounded overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap',
            contentBg,
          )}
        >
          {getContent()}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
});

export function ConversationPane({
  messages,
  isProcessing,
  suggestedNextSteps,
  streamingText,
  verboseHistory,
  pendingVerboseEvents,
  onSuggestionClick,
  emptyStateContent,
  headerContent,
  footerContent,
  className,
}: ConversationPaneProps) {
  // Get streaming content from 'builder' nodeId
  const currentStreamingContent = streamingText?.['builder'] || '';

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll the messages container to the bottom without affecting the parent
  // horizontal scroll (which would pull mobile users back to the conversation panel).
  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamingContent, scrollToBottom]);

  // Scroll for verbose events separately: only when the first event appears (0 → 1+),
  // not on every subsequent addition, to avoid repeated scroll animations.
  const prevVerboseCountRef = useRef(0);
  useEffect(() => {
    const count = pendingVerboseEvents?.length || 0;
    if (count > 0 && prevVerboseCountRef.current === 0) {
      scrollToBottom();
    }
    prevVerboseCountRef.current = count;
  }, [pendingVerboseEvents?.length, scrollToBottom]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header content (e.g. guided panels) */}
      {headerContent}
      {/* Messages area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          emptyStateContent ? (
            emptyStateContent
          ) : (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="max-w-lg space-y-4">
                <h3 className="text-lg font-semibold text-foreground text-center">
                  Hello! I&apos;m the Fias Arche Builder.
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground text-center">
                  I help you create interactive AI-powered applications called Arches. Just describe
                  what you have in mind, and I&apos;ll help you bring it to life with the
                  appropriate UI components and workflow logic!
                </p>
                <p className="text-sm font-medium text-foreground">I can help you:</p>
                <ul className="text-sm space-y-2 text-muted-foreground list-disc pl-5">
                  <li>
                    <strong className="text-foreground">Design user interfaces</strong> using
                    components like forms, buttons, cards, tables, and more
                  </li>
                  <li>
                    <strong className="text-foreground">Create workflows</strong> that orchestrate
                    AI Entity interactions
                  </li>
                  <li>
                    <strong className="text-foreground">Build complete Arches</strong> from concept
                    to implementation
                  </li>
                  <li>
                    <strong className="text-foreground">Iterate and refine</strong> your designs
                    based on feedback
                  </li>
                  <li>
                    <strong className="text-foreground">Share and earn</strong> — use Arches
                    yourself, or make them available to others and earn based on their use
                  </li>
                </ul>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  What kind of Arche would you like to build today?
                </p>
              </div>
            </div>
          )
        ) : (
          messages.map((message, index) => (
            <Fragment key={index}>
              {/* Show committed verbose events before the assistant message they produced */}
              {message.role === 'assistant' &&
                verboseHistory?.[message.timestamp] &&
                verboseHistory[message.timestamp].length > 0 && (
                  <div className="space-y-1.5 my-2">
                    {verboseHistory[message.timestamp].map((event, i) => (
                      <VerboseEventDisplay
                        key={`${message.timestamp}-${event.type}-${i}`}
                        event={event}
                      />
                    ))}
                  </div>
                )}
              <MessageBubble message={message} />
            </Fragment>
          ))
        )}

        {/* Pending verbose events for in-progress response */}
        {pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
          <div className="space-y-1.5 my-2">
            {pendingVerboseEvents.map((event, index) => (
              <VerboseEventDisplay key={`pending-${event.type}-${index}`} event={event} />
            ))}
          </div>
        )}

        {/* Streaming response or processing indicator */}
        {isProcessing &&
          (currentStreamingContent ? (
            <MessageBubble
              message={{
                role: 'assistant',
                content: currentStreamingContent,
                timestamp: new Date().toISOString(),
              }}
              isStreaming
            />
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Fias AI is thinking...</span>
            </div>
          ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {suggestedNextSteps && suggestedNextSteps.length > 0 && !isProcessing && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-2">
            {suggestedNextSteps.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Footer content (e.g. guided controls) */}
      {footerContent}
    </div>
  );
}
